import { SlideHeader } from "../../../SlideHeader";
import { SupLink } from "../../../SlideContext";

const LADDER: Array<{
  label: string;
  desc: string;
  amount: string;
  beachhead?: boolean;
}> = [
  {
    label: "Sector — mobile app marketing spend",
    desc: "$78B paid UA + $31B remarketing",
    amount: "$109B/yr",
  },
  {
    label: "Addressable tooling TAM",
    desc: "ASO + MMP + UA tooling + analytics + creative-gen SaaS",
    amount: "~$5–15B",
  },
  {
    label: "ASO tooling — our beachhead",
    desc: "Where we start · $1.9–7.6B by 2035",
    amount: "$0.5–1.0B",
    beachhead: true,
  },
];

const EXPANSION = [
  "ASO",
  "UA brief automation",
  "MMP integration",
  "AI creative for ads",
];

export default function TheWiderPlay() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-7">
      <SlideHeader eyebrow="The Wider Play">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-5xl md:leading-[1.1]">
          ASO is our beachhead on the{" "}
          <span className="text-blue-700">
            $100B+ mobile app marketing landscape
          </span>
          .
        </h1>
        <p className="mt-3 text-xl font-medium tracking-tight text-slate-700 md:mt-4 md:text-3xl">
          We start where we can win — owning the data layer the wider play is
          built on.
        </p>
      </SlideHeader>

      {/* The Ladder */}
      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 md:mb-4 md:text-sm md:tracking-[0.25em]">
          The Ladder
        </p>
        <div className="space-y-3 md:space-y-4">
          {LADDER.map((row, i) => {
            const widthClass =
              i === 0
                ? "w-full"
                : i === 1
                ? "ml-auto w-full md:w-[88%]"
                : "ml-auto w-full md:w-[76%]";
            return (
              <div key={row.label} className={widthClass}>
                <LadderRow
                  {...row}
                  sup={i === 0 ? "15" : i === 1 ? "16" : undefined}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* The Expansion Path */}
      <div className="mt-4 border-t border-slate-200 pt-4 md:mt-4 md:pt-5">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 md:mb-4 md:text-sm md:tracking-[0.25em]">
          The Expansion Path
          <SupLink to="expansion-path">E</SupLink>
        </p>
        <div className="flex flex-wrap items-center gap-2 text-sm md:gap-3 md:text-base">
          {EXPANSION.map((step, i) => (
            <span key={step} className="flex items-center gap-2 md:gap-3">
              <span
                className={
                  i === 0
                    ? "rounded-full bg-blue-100 px-3 py-1.5 font-semibold text-blue-700 md:px-4 md:py-2"
                    : "rounded-full bg-white px-3 py-1.5 text-slate-700 ring-1 ring-slate-300 md:px-4 md:py-2"
                }
              >
                {step}
              </span>
              {i < EXPANSION.length - 1 ? (
                <span className="text-base text-slate-400 md:text-xl">→</span>
              ) : null}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function LadderRow({
  label,
  desc,
  amount,
  beachhead,
  sup,
}: {
  label: string;
  desc: string;
  amount: string;
  beachhead?: boolean;
  sup?: string;
}) {
  const wrapClass = beachhead
    ? "rounded-xl bg-blue-50 p-4 ring-2 ring-blue-400 md:p-5"
    : "rounded-xl bg-white p-4 ring-1 ring-slate-200 md:p-5";
  const labelClass = beachhead
    ? "text-xs font-bold uppercase tracking-[0.15em] text-blue-700 md:text-sm"
    : "text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 md:text-sm";
  const descClass = beachhead
    ? "mt-1 text-sm text-blue-900 md:text-base"
    : "mt-1 text-sm text-slate-600 md:text-base";
  const amountClass = beachhead
    ? "text-2xl font-bold tracking-tight text-blue-700 md:text-4xl"
    : "text-2xl font-bold tracking-tight text-slate-900 md:text-4xl";

  return (
    <div className={wrapClass}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className={labelClass}>
            {label}
            {sup ? <SupLink to="sources">{sup}</SupLink> : null}
          </p>
          <p className={descClass}>{desc}</p>
        </div>
        <p className={amountClass}>{amount}</p>
      </div>
    </div>
  );
}
